import { CommonModule, Location } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AdminTenantSelectComponent,
  TenantSelectItem,
} from '@app/components/admin-tenant-select';
import { DropdownComponent } from '@app/components/dropdowns';
import { LogoComponent } from '@app/components/logo';
import { AdminBootstrapResolver } from '@app/resolvers/admin-bootstrap.resolver';
import { AdminAuthService } from '@app/services/admin-auth/admin-auth.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminUserService } from '@app/services/admin-user/admin-user.service';
import { Logger } from '@app/services/logger/logger.service';
import { map, take } from 'rxjs';

enum DropdownMenuOption {
  logOut = 'logOut',
}

@Component({
  standalone: true,
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.css'],
  imports: [
    CommonModule,
    DropdownComponent,
    LogoComponent,
    AdminTenantSelectComponent,
  ],
})
export class TopbarComponent {
  private adminAuthService = inject(AdminAuthService);
  private adminTenantService = inject(AdminTenantService);
  private userService = inject(AdminUserService);
  private route = inject(ActivatedRoute);
  private logger = inject(Logger);
  private location = inject(Location);
  private router = inject(Router);
  private adminBootstrapResolver = inject(AdminBootstrapResolver);

  @Input() user!: { name: string };
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef;

  isDropdownOpen = false;
  dropdownMenuOptions = Object.values(DropdownMenuOption);

  private initialData = toSignal(
    this.route.data.pipe(map((data) => data.initialData))
  );

  logoUrl = computed(() => this.initialData()?.theme?.light?.primaryLogo?.src);

  tenants = this.adminTenantService.tenants;
  showTenants = computed(
    () =>
      this.userService.isCurrentUserSuperAdmin() && this.tenants().length > 0
  );

  private userSelectedTenantName = signal<string | undefined>(undefined);
  private derivedTenantName = computed(
    () => this.adminTenantService.getTenant()?.name ?? ''
  );

  selectedTenant = computed(
    () => this.userSelectedTenantName() ?? this.derivedTenantName()
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const list = this.tenants();
      if (!list.length) return;

      const storedId = this.adminTenantService.getTenantIdFromStorage();
      if (!storedId) {
        const defaultTenant = list.find(
          (t) => t.id === this.adminTenantService.getDefaultTenantId()
        );
        if (defaultTenant) {
          this.adminTenantService.setTenantIdInStorage(defaultTenant);
        }
      }
    });
    this.adminTenantService
      .loadTenants()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => this.logger.error('Error loading tenants'),
      });
  }

  handleToggle() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  handleSelected(option: DropdownMenuOption) {
    if (option === DropdownMenuOption.logOut) {
      this.adminAuthService.signOutAndRedirect();
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      !this.dropdownMenu.nativeElement.contains(target) &&
      this.isDropdownOpen
    ) {
      this.handleToggle();
    }
  }

  reloadComponent() {
    const currentUrl = this.location.path();
    this.adminBootstrapResolver
      .refresh()
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.userSelectedTenantName.set(undefined);
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => this.router.navigate([currentUrl]));
        }
      });
  }

  onSelect(selectedTenant: TenantSelectItem) {
    const fullTenant = this.tenants().find((t) => t.id === selectedTenant.id);
    if (fullTenant) {
      this.userSelectedTenantName.set(selectedTenant.name);
      this.adminTenantService.setTenantIdInStorage(fullTenant);
      this.reloadComponent();
    } else {
      this.logger.error(
        `Could not find full tenant for id ${selectedTenant.id} in onSelect`
      );
    }
  }
}
